import React, { useState, useRef } from "react";
import { withStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import backgroundImage from "~/images/modal-background-image.png";
import { Close } from "@mui/icons-material";
import { buttonClass } from "../../utils/Buttons";
import useOutsideClick from "../../Hooks/useOutsideClick";
import ProfileCard from "../../ProfileCard";

const CustomField = withStyles({
  root: {
    marginTop: 8,
    backgroundColor: "#ffffff",
    borderRadius: 8,
    "& .MuiOutlinedInput-input": {
      padding: "12px 16px",
      fontSize: 14,
      fontWeight: 500,
      color: "#25265e",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "green",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      "& fieldset": {
        borderColor: "#DEE7EE",
      },
      "&:hover fieldset": {
        borderColor: "#DEE7EE",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#3D71FF",
      },
    },
    "& .MuiOutlinedInput-input::placeholder": {
      color: "#718291",
      fontSize: 14,
      fontWeight: 400,
      fontFamily: '"Roboto", sans-serif',
    },
  },
})(TextField);

const FieldLabel = ({ title }) => {
  return (
    <div className="t--sm t--subtitle t--500">
      {title.toUpperCase()} <span style={{ color: "#e91e63" }}>*</span>
    </div>
  );
};

const ModalUserInvite = ({
  closeModal,
  organization_id,
  entity,
  setUsers,
  setNotice,
  csrfToken,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [newUser, setNewUser] = useState({
    first_name: "",
    last_name: "",
    email: "",
    role: "user",
  });
  const [hasError, setHasError] = useState(false);
  const [errorText, setErrorText] = useState("");

  const modalRef = useRef(null);
  useOutsideClick(modalRef, closeModal);

  const updateNewUser = (key, value) => {
    setNewUser((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const isDisabled = () => {
    return submitting;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      validateForm();

      const response = await fetch(`/organization/${organization_id}/entities/${entity.id}/users`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute("content"),
        },
        body: JSON.stringify({ user: { ...newUser } }),
      });
      const data = await response.json();
      if (response.ok) {
        setUsers((prev) => [...prev, data.user]);
        setNotice({ kind: "success", open: true, message: "User invite successful." });
        closeModal();
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      console.error(error);
      setNotice({
        kind: "error",
        open: true,
        message: error.message || "Error occurred - please try again",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const validateForm = () => {
    if (isValidEmail()) {
      setHasError(false);
      setErrorText("");
      return;
    }

    const invalidCharacters = invalidEmailCharacters();
    if (invalidCharacters.length > 0) {
      setHasError(true);
      setErrorText(
        `Email contains invalid characters - Please remove: ${invalidCharacters.join(", ")}`
      );
    } else {
      setHasError(true);
      setErrorText("Email is not valid - Please verify your input");
    }

    throw new Error();
  };

  const isValidEmail = () => {
    // emailRegex is adapted from URI::MailTo::EMAIL_REGEXP in Ruby
    const emailRegex =
      /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
    return emailRegex.test(newUser.email);
  };

  const invalidEmailCharacters = () => {
    const invalidCharacterRegExp = /[()\[\];:",<>\\]/g;
    const matchSet = new Set(newUser.email.match(invalidCharacterRegExp));
    return Array.from(matchSet);
  };

  const renderRoleOptions = () => {
    return (
      <CustomField
        select
        label="Role"
        margin="normal"
        variant="outlined"
        id="role"
        name="user[role]"
        fullWidth
        required
        SelectProps={{
          native: true,
        }}
        value={newUser.role}
        onChange={(event) => {
          updateNewUser("role", event.target.value);
        }}
      >
        <option key="User" value="user">
          User
        </option>
        <option key="Admin" value="admin">
          Admin
        </option>
      </CustomField>
    );
  };

  return (
    <div className="tpa-modal__background">
      <div className="tpa-modal__container" ref={modalRef}>
        <div className="tpa-modal__header">
          <div className="tpa-modal__header__img">
            <img src={backgroundImage} width={145} />
          </div>
          <div className="button button--sq-md button--transparent" onClick={closeModal}>
            <Close />
          </div>
        </div>
        <form
          method="POST"
          id="inviteUserForm"
          action={`/organization/${organization_id}/users`}
          onSubmit={handleSubmit}
        >
          <div className="tpa-modal__body">
            <div className="flex flex-column gap--16">
              <div className="t--xl t--500">Invite a Team Member</div>
              <div className="t--subtitle">
                To send an invite to join your entity's 340B ESP™ account, enter contact information
                and select their role.
              </div>
              <ProfileCard
                initials={entity.entity_name[0]}
                title={entity.entity_name}
                subtitle={entity.id_340b}
              />
            </div>
            <div className="flex flex-column gap--24">
              <div className="flex flex-column gap--16">
                <div className="t--16 t--500">Basic Info</div>
                <div className="flex gap--24">
                  <div style={{ flex: 1 }}>
                    <FieldLabel title="first name" />
                    <CustomField
                      variant="outlined"
                      id="firstName"
                      placeholder="First Name"
                      name="user[first_name]"
                      autoFocus
                      fullWidth
                      required
                      value={newUser.first_name}
                      onChange={(event) => updateNewUser("first_name", event.target.value)}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <FieldLabel title="last name" />
                    <CustomField
                      variant="outlined"
                      id="lastName"
                      placeholder="Last Name"
                      name="user[last_name]"
                      fullWidth
                      required
                      value={newUser.last_name}
                      onChange={(event) => updateNewUser("last_name", event.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div>
                <FieldLabel title="email address" />
                <CustomField
                  variant="outlined"
                  id="email"
                  placeholder="Email Address"
                  name="user[email]"
                  error={hasError}
                  fullWidth
                  required
                  helperText={hasError ? errorText : null}
                  onChange={(event) => updateNewUser("email", event.target.value)}
                />
              </div>
              <div>
                <FieldLabel title="role" />
                <CustomField
                  select
                  variant="outlined"
                  id="role"
                  name="user[role]"
                  fullWidth
                  required
                  SelectProps={{
                    native: true,
                  }}
                  value={newUser.role}
                  onChange={(event) => {
                    updateNewUser("role", event.target.value);
                  }}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </CustomField>
              </div>
            </div>
            <div className="flex justify-content-end gap--16">
              <button className="button button--white t--500" onClick={closeModal}>
                Cancel
              </button>
              <button type="submit" className={buttonClass(isDisabled())} disabled={isDisabled()}>
                Send Invite
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalUserInvite;
